// Order Notification Service
// Sends notifications via WhatsApp and SMS on order status changes

import whatsappService from './communication/whatsappService';
import smsService from './communication/smsService';

class OrderNotificationService {
  constructor() {
    this.enableWhatsApp = process.env.REACT_APP_ENABLE_WHATSAPP_NOTIFICATIONS === 'true';
    this.enableSMS = process.env.REACT_APP_ENABLE_SMS_NOTIFICATIONS === 'true';
    this.fallbackToSMS = process.env.REACT_APP_WHATSAPP_FALLBACK_SMS === 'true';

    console.log('📢 Order Notification Service initialized');
    console.log(`   WhatsApp: ${this.enableWhatsApp ? '✅ Enabled' : '❌ Disabled'}`);
    console.log(`   SMS: ${this.enableSMS ? '✅ Enabled' : '❌ Disabled'}`);
  }

  /**
   * Send order status update notification
   * @param {Object} orderData - Full order object
   * @param {string} newStatus - New status
   */
  async notifyStatusChange(orderData, newStatus) {
    if (!orderData?.customer?.phone) {
      console.warn('⚠️ Cannot send notification: No customer phone');
      return { success: false, error: 'No customer phone' };
    }

    const results = {
      whatsapp: null,
      sms: null,
      success: false
    };

    // Try WhatsApp first
    if (this.enableWhatsApp) {
      try {
        results.whatsapp = await whatsappService.sendOrderStatusUpdate(orderData, newStatus);
        if (results.whatsapp.success) {
          results.success = true;
        }
      } catch (error) {
        console.error('WhatsApp notification failed:', error);
        results.whatsapp = { success: false, error: error.message };
      }
    }

    // Send SMS (as fallback or in addition)
    if (this.enableSMS || (this.fallbackToSMS && !results.whatsapp?.success)) {
      try {
        results.sms = await smsService.sendOrderStatusUpdate(orderData, newStatus);
        if (results.sms.success) {
          results.success = true;
        }
      } catch (error) {
        console.error('SMS notification failed:', error);
        results.sms = { success: false, error: error.message };
      }
    }

    // Log results
    if (results.success) {
      console.log(`✅ Notification sent for order #${orderData.orderNumber} - Status: ${newStatus}`);
    } else {
      console.warn(`⚠️ Failed to send notification for order #${orderData.orderNumber}`);
    }

    return results;
  }

  /**
   * Send order confirmation notification
   * @param {Object} orderData - Full order object
   */
  async notifyOrderConfirmed(orderData) {
    if (!orderData?.customer?.phone) {
      return { success: false, error: 'No customer phone' };
    }

    const results = { whatsapp: null, sms: null, success: false };

    if (this.enableWhatsApp) {
      try {
        results.whatsapp = await whatsappService.sendOrderConfirmation(orderData);
        if (results.whatsapp.success) results.success = true;
      } catch (error) {
        results.whatsapp = { success: false, error: error.message };
      }
    }

    if (this.enableSMS || (this.fallbackToSMS && !results.whatsapp?.success)) {
      try {
        results.sms = await smsService.sendOrderConfirmation(orderData);
        if (results.sms.success) results.success = true;
      } catch (error) {
        results.sms = { success: false, error: error.message };
      }
    }

    return results;
  }

  /**
   * Send delivery assigned notification
   * @param {Object} orderData - Full order object
   * @param {Object} deliveryBoyData - Delivery partner info
   */
  async notifyDeliveryAssigned(orderData, deliveryBoyData) {
    if (!orderData?.customer?.phone) {
      return { success: false, error: 'No customer phone' };
    }

    const results = { whatsapp: null, sms: null, success: false };

    if (this.enableWhatsApp) {
      try {
        results.whatsapp = await whatsappService.sendDeliveryAssigned(orderData, deliveryBoyData);
        if (results.whatsapp.success) results.success = true;
      } catch (error) {
        results.whatsapp = { success: false, error: error.message };
      }
    }

    if (this.enableSMS || (this.fallbackToSMS && !results.whatsapp?.success)) {
      try {
        results.sms = await smsService.sendDeliveryAssigned(orderData, deliveryBoyData);
        if (results.sms.success) results.success = true;
      } catch (error) {
        results.sms = { success: false, error: error.message };
      }
    }

    return results;
  }

  /**
   * Send order bill via WhatsApp
   * @param {Object} orderData - Full order object
   */
  async sendOrderBill(orderData) {
    if (!orderData?.customer?.phone) {
      return { success: false, error: 'No customer phone' };
    }

    const results = { whatsapp: null, success: false };

    if (this.enableWhatsApp) {
      try {
        results.whatsapp = await whatsappService.sendOrderBill(orderData);
        if (results.whatsapp.success) results.success = true;
      } catch (error) {
        console.error('Failed to send order bill:', error);
        results.whatsapp = { success: false, error: error.message };
      }
    }

    return results;
  }

  /**
   * Send UPI payment QR code for COD orders
   * @param {Object} orderData - Full order object
   */
  async sendPaymentQRCode(orderData) {
    if (!orderData?.customer?.phone) {
      return { success: false, error: 'No customer phone' };
    }

    // Only send for COD orders
    if (orderData.paymentMethod !== 'cod') {
      return { success: false, error: 'Not a COD order' };
    }

    const results = { whatsapp: null, success: false };

    if (this.enableWhatsApp) {
      try {
        results.whatsapp = await whatsappService.sendPaymentQRCode(orderData);
        if (results.whatsapp.success) results.success = true;
      } catch (error) {
        console.error('Failed to send payment QR:', error);
        results.whatsapp = { success: false, error: error.message };
      }
    }

    return results;
  }

  /**
   * Send complete order package (Bill + Payment info for COD)
   * @param {Object} orderData - Full order object
   */
  async sendCompleteOrderPackage(orderData) {
    console.log('📦 Sending complete order package...');

    // Send order bill
    const billResult = await this.sendOrderBill(orderData);

    // If COD, also send payment QR code
    if (orderData.paymentMethod === 'cod') {
      // Wait a bit before sending QR to avoid spam
      await new Promise(resolve => setTimeout(resolve, 2000));
      const qrResult = await this.sendPaymentQRCode(orderData);

      return {
        bill: billResult,
        qr: qrResult,
        success: billResult.success || qrResult.success
      };
    }

    return {
      bill: billResult,
      success: billResult.success
    };
  }

  /**
   * Check if notifications are configured
   */
  isConfigured() {
    return (this.enableWhatsApp && whatsappService.isConfigured()) ||
           (this.enableSMS && smsService.isConfigured());
  }

  /**
   * Get notification settings status
   */
  getStatus() {
    return {
      whatsapp: {
        enabled: this.enableWhatsApp,
        configured: whatsappService.isConfigured()
      },
      sms: {
        enabled: this.enableSMS,
        configured: smsService.isConfigured()
      },
      fallbackToSMS: this.fallbackToSMS
    };
  }
}

// Singleton instance
const orderNotificationService = new OrderNotificationService();

export default orderNotificationService;
